import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, AlertCircle, Heart, Phone, Globe, Users, CheckCircle } from 'lucide-react';
import SEOHead from '../../../components/SEO/SEOHead';
import SEOFooter from '../../../components/SEO/SEOFooter';

const JogoResponsavel = () => {
  return (
    <>
      <SEOHead 
        customTitle="Jogo Responsável - Aposte com Segurança com Orientação de IA | PickStar AI"
        customDescription="Aprenda sobre práticas responsáveis de apostas esportivas. Obtenha ajuda com problemas de jogo, defina limites e use IA para tomar decisões informadas de apostas."
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
        {/* Header */}
        <header className="bg-black/50 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Voltar ao Início
            </Link>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-emerald-400" />
              <h1 className="text-4xl md:text-5xl font-bold text-white">
                Jogo Responsável
              </h1>
            </div>
            <p className="text-xl text-gray-300 mb-8">
              Seu bem-estar vem primeiro. Aposte inteligente, aposte seguro, aposte responsavelmente.
            </p>
          </div>
        </section>

        {/* Main Content */}
        <main className="px-4 pb-16">
          <div className="max-w-4xl mx-auto space-y-12">
            
            {/* Our Commitment */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-4">Nosso Compromisso com o Jogo Responsável</h2>
              <p className="text-gray-300 mb-4">
                Na PickStar AI, acreditamos que as apostas esportivas devem ser uma atividade divertida e prazerosa. 
                Estamos comprometidos em promover práticas de jogo responsável e fornecer ferramentas para ajudar nossos usuários 
                a manter controle sobre suas atividades de apostas.
              </p>
              <p className="text-gray-300">
                Nossa tecnologia de IA é projetada não apenas para melhorar seu sucesso nas apostas, mas também para encorajar 
                decisões inteligentes e informadas baseadas em dados ao invés de emoção ou impulso.
              </p>
            </section>

            {/* Know Your Limits */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-6 h-6 text-emerald-400" />
                <h2 className="text-2xl font-bold text-white">Conheça Seus Limites</h2>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-lg p-6 border border-emerald-500/20">
                  <h3 className="text-lg font-semibold text-white mb-3">Defina Limites Pessoais</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">Limites de Tempo</p>
                          <p className="text-gray-300 text-sm">Decida quanto tempo por dia/semana gastar apostando</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">Limites de Perda</p>
                          <p className="text-gray-300 text-sm">Defina um valor máximo que você pode perder</p>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">Limites de Depósito</p>
                          <p className="text-gray-300 text-sm">Controle quanto dinheiro você adiciona às contas de apostas</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                        <div>
                          <p className="text-white font-medium">Limites de Valor de Aposta</p>
                          <p className="text-gray-300 text-sm">Nunca aposte mais que 1-3% da sua banca</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-black/30 rounded-lg p-6 border border-white/10">
                  <h3 className="text-lg font-semibold text-white mb-3">Regras de Ouro</h3>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>Nunca aposte dinheiro que você não pode perder</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>Não aposte para recuperar perdas (perseguir perdas)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>Nunca pegue dinheiro emprestado para apostar</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>Não aposte sob influência de álcool ou drogas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>Faça pausas regulares das apostas</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-400">•</span>
                      <span>Veja apostas como entretenimento, não como forma de ganhar dinheiro</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Warning Signs */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="w-6 h-6 text-amber-400" />
                <h2 className="text-2xl font-bold text-white">Reconheça os Sinais de Alerta</h2>
              </div>
              
              <p className="text-gray-300 mb-6">
                O vício em jogos pode se desenvolver gradualmente. Esteja atento a estes sinais de alerta:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                  <h3 className="font-semibold text-red-400 mb-2">Sinais Comportamentais</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Gastar mais tempo ou dinheiro do que o pretendido</li>
                    <li>• Mentir sobre atividades de apostas</li>
                    <li>• Negligenciar responsabilidades para apostar</li>
                    <li>• Apostar para escapar de problemas ou emoções</li>
                    <li>• Tentativas sem sucesso de parar ou reduzir apostas</li>
                  </ul>
                </div>
                
                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                  <h3 className="font-semibold text-red-400 mb-2">Sinais Emocionais</h3>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Sentir ansiedade quando não está apostando</li>
                    <li>• Mudanças de humor relacionadas a ganhos/perdas</li>
                    <li>• Depressão ou irritabilidade</li>
                    <li>• Perda de interesse em outras atividades</li>
                    <li>• Sentimentos de culpa ou vergonha sobre apostas</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <p className="text-amber-300 text-sm">
                  <strong>Importante:</strong> Se você reconhece qualquer um desses sinais em si mesmo ou em alguém que conhece, 
                  é importante buscar ajuda imediatamente. O vício em jogos é tratável e apoio está disponível.
                </p>
              </div>
            </section>

            {/* How PickStar AI Helps */}
            <section className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 backdrop-blur-sm rounded-xl p-8 border border-emerald-500/20">
              <h2 className="text-2xl font-bold text-white mb-6">Como o PickStar AI Promove Jogo Responsável</h2>
              
              <div className="space-y-4">
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Decisões Baseadas em Dados</h3>
                  <p className="text-gray-300 text-sm">
                    Nossa IA remove a emoção das decisões de apostas, ajudando você a fazer escolhas racionais baseadas em 
                    estatísticas e probabilidade ao invés de sentimentos ou desespero.
                  </p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Ferramentas de Gestão de Banca</h3>
                  <p className="text-gray-300 text-sm">
                    Fornecemos orientação sobre dimensionamento adequado de apostas e gestão de banca para ajudá-lo a permanecer 
                    dentro de seus limites e evitar perdas catastróficas.
                  </p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Conteúdo Educacional</h3>
                  <p className="text-gray-300 text-sm">
                    Nossa plataforma educa usuários sobre probabilidade, valor esperado e as realidades das apostas 
                    esportivas para estabelecer expectativas adequadas.
                  </p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Ambiente Sem Pressão</h3>
                  <p className="text-gray-300 text-sm">
                    Nunca pressionamos usuários a apostar. Nossa IA fornece análise e previsões, mas a decisão 
                    de apostar sempre permanece sua.
                  </p>
                </div>
              </div>
            </section>

            {/* Self-Assessment */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-emerald-400" />
                <h2 className="text-2xl font-bold text-white">Perguntas de Autoavaliação</h2>
              </div>
              
              <p className="text-gray-300 mb-6">
                Faça estas perguntas a si mesmo honestamente para avaliar sua relação com apostas:
              </p>
              
              <div className="space-y-3">
                <div className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Você aposta mais do que pode perder?</p>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Você já mentiu para família ou amigos sobre suas apostas?</p>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Você sente necessidade de apostar valores maiores para ter emoção?</p>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Você tentou reduzir as apostas mas não conseguiu?</p>
                </div>
                <div className="bg-black/30 rounded-lg p-4 border-l-4 border-emerald-500">
                  <p className="text-gray-300">Você aposta para escapar de preocupações, tédio ou depressão?</p>
                </div>
              </div>
              
              <p className="text-amber-300 mt-6 text-sm">
                Se você respondeu "sim" a qualquer uma dessas perguntas, pode querer avaliar seus hábitos de apostas 
                e considerar buscar apoio.
              </p>
            </section>

            {/* Getting Help */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <div className="flex items-center gap-3 mb-6">
                <Phone className="w-6 h-6 text-emerald-400" />
                <h2 className="text-2xl font-bold text-white">Obtendo Ajuda e Apoio</h2>
              </div>
              
              <p className="text-gray-300 mb-6">
                Se você ou alguém que conhece precisa de ajuda com vício em jogos, estes recursos estão disponíveis 24/7:
              </p>
              
              <div className="space-y-4">
                <div className="bg-emerald-500/10 rounded-lg p-6 border border-emerald-500/30">
                  <h3 className="font-semibold text-white mb-3">Brasil</h3>
                  <div className="space-y-2 text-gray-300">
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-400" />
                      <strong>CVV - Centro de Valorização da Vida:</strong> 188
                    </p>
                    <p className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-emerald-400" />
                      <strong>Jogadores Anônimos:</strong>{' '}
                      <a href="https://www.jogadoresanonimos.org.br" target="_blank" rel="noopener noreferrer" 
                         className="text-emerald-400 hover:text-emerald-300">
                        www.jogadoresanonimos.org.br
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-emerald-400" />
                      <strong>CAPS - Centro de Atenção Psicossocial:</strong>{' '}
                      <span className="text-gray-300">Unidades de saúde mental regionais</span>
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-emerald-400" />
                      <strong>Disque Saúde (SUS):</strong> 136
                    </p>
                  </div>
                </div>
                
                <div className="bg-violet-500/10 rounded-lg p-6 border border-violet-500/30">
                  <h3 className="font-semibold text-white mb-3">Recursos Internacionais</h3>
                  <div className="space-y-2 text-gray-300">
                    <p><strong>Estados Unidos:</strong> National Problem Gambling Helpline - 1-800-522-4700</p>
                    <p><strong>Reino Unido:</strong> GamCare - 0808 8020 133</p>
                    <p><strong>Austrália:</strong> Gambling Help - 1800 858 858</p>
                    <p><strong>Canadá:</strong> ConnexOntario - 1-866-531-2600</p>
                    <p><strong>Portugal:</strong> SOS Voz Amiga - 213 544 545</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Tools and Resources */}
            <section className="bg-white/5 backdrop-blur-sm rounded-xl p-8 border border-white/10">
              <h2 className="text-2xl font-bold text-white mb-6">Ferramentas e Recursos Úteis</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Programas de Autoexclusão</h3>
                  <p className="text-gray-300 text-sm">
                    A maioria dos sites de apostas oferece opções de autoexclusão para bloquear temporária ou permanentemente 
                    o acesso à sua conta.
                  </p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Software de Bloqueio</h3>
                  <p className="text-gray-300 text-sm">
                    Softwares como GamBlock ou Betfilter podem bloquear o acesso a sites de apostas em seus dispositivos.
                  </p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Controles Financeiros</h3>
                  <p className="text-gray-300 text-sm">
                    Peça ao seu banco sobre bloqueios de transações de apostas ou tenha alguém de confiança gerenciando suas finanças.
                  </p>
                </div>
                
                <div className="bg-black/30 rounded-lg p-4">
                  <h3 className="font-semibold text-emerald-300 mb-2">Grupos de Apoio</h3>
                  <p className="text-gray-300 text-sm">
                    Participe de grupos de apoio locais ou online para se conectar com outros que entendem sua luta.
                  </p>
                </div>
              </div>
            </section>

            {/* Final Message */}
            <section className="text-center py-8 bg-gradient-to-r from-emerald-500/10 to-violet-500/10 rounded-xl p-8 border border-emerald-500/20">
              <h2 className="text-2xl font-bold text-white mb-4">Lembre-se: Seu Bem-Estar Vem Primeiro</h2>
              <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
                As apostas esportivas devem aumentar seu prazer com os esportes, não prejudicar sua vida. Se parar de ser 
                divertido, é hora de fazer uma pausa. Estamos aqui para fornecer análise inteligente, mas sua saúde, 
                relacionamentos e segurança financeira devem sempre ser sua prioridade máxima.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/"
                  className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/20 transition-all border border-white/20"
                >
                  Voltar ao Início
                </Link>
                <a 
                  href="https://www.jogadoresanonimos.org.br"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-emerald-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-emerald-600 transition-all"
                >
                  Encontrar Ajuda Próxima
                </a>
              </div>
            </section>
          </div>
        </main>

        {/* Footer */}
        <SEOFooter language="pt-BR" />
      </div>
    </>
  );
};

export default JogoResponsavel;